import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/apiService';
import './ClaimsForm.css';

interface ClaimData {
  claimType: string;
  policyNumber: string;
  dateOfIncident: string;
  timeOfIncident: string;
  location: string;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  damageAmount: string;
  witnesses: string;
  policeReport: boolean;
  policeReportNumber: string;
  files: File[];
}

interface ClaimSubmission {
  claimNumber: string;
  status: string;
  submittedDate: string;
  estimatedProcessingTime: string;
}

const ClaimsForm: React.FC = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [claimSubmission, setClaimSubmission] = useState<ClaimSubmission | null>(null);
  const [errors, setErrors] = useState<Partial<ClaimData>>({});
  
  const [formData, setFormData] = useState<ClaimData>({
    claimType: '',
    policyNumber: '',
    dateOfIncident: '',
    timeOfIncident: '',
    location: '',
    description: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    damageAmount: '',
    witnesses: '',
    policeReport: false,
    policeReportNumber: '',
    files: []
  });

  const claimTypes = [
    { value: 'auto', label: 'Auto Insurance Claim', icon: '🚗', desc: 'Vehicle accidents, theft, or damage' },
    { value: 'home', label: 'Home Insurance Claim', icon: '🏠', desc: 'Property damage, theft, or natural disasters' },
    { value: 'health', label: 'Health Insurance Claim', icon: '🏥', desc: 'Medical expenses and treatments' },
    { value: 'life', label: 'Life Insurance Claim', icon: '👨‍👩‍👧‍👦', desc: 'Life insurance benefits' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name as keyof ClaimData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData(prev => ({
        ...prev,
        files: [...prev.files, ...newFiles]
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<ClaimData> = {};

    if (step === 1) {
      if (!formData.claimType) newErrors.claimType = 'Please select a claim type';
      if (!formData.policyNumber) newErrors.policyNumber = 'Policy number is required';
      if (formData.policyNumber && !/^[A-Z0-9]{8,12}$/.test(formData.policyNumber)) {
        newErrors.policyNumber = 'Policy number must be 8-12 alphanumeric characters';
      }
    }

    if (step === 2) {
      if (!formData.dateOfIncident) newErrors.dateOfIncident = 'Date of incident is required';
      if (!formData.timeOfIncident) newErrors.timeOfIncident = 'Time of incident is required';
      if (!formData.location) newErrors.location = 'Location is required';
      if (!formData.description || formData.description.length < 20) {
        newErrors.description = 'Please provide a detailed description (minimum 20 characters)';
      }
    }

    if (step === 3) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone) newErrors.phone = 'Phone number is required';
      if (formData.phone && !/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    setIsLoading(true);

    try {
      // Create claim data in the format expected by our backend
      const claimData = {
        policyNumber: formData.policyNumber,
        type: formData.claimType,
        incidentDate: formData.dateOfIncident,
        incidentDescription: formData.description,
        incidentLocation: formData.location || '',
        claimAmount: parseFloat(formData.damageAmount),
        policeReportNumber: formData.policeReportNumber || '',
        priority: 'normal'
      };

      const response = await ApiService.createClaim(claimData);

      if (response.success && response.data) {
        const submission: ClaimSubmission = {
          claimNumber: response.data.claimId.substring(0, 8),
          status: 'Under Review',
          submittedDate: new Date().toLocaleDateString(),
          estimatedProcessingTime: '5-7 business days'
        };

        setClaimSubmission(submission);
        setIsSubmitted(true);
      } else {
        throw new Error(response.error || 'Failed to submit claim');
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      alert('Failed to submit claim. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className={`step ${currentStep >= step ? 'active' : ''}`}>
          <div className="step-number">{step}</div>
          <div className="step-label">
            {step === 1 && 'Claim Type'}
            {step === 2 && 'Incident Details'}
            {step === 3 && 'Contact Info'}
            {step === 4 && 'Documents'}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="form-step">
      <h2>What type of claim are you filing?</h2>
      <div className="claim-types">
        {claimTypes.map((type) => (
          <div
            key={type.value}
            className={`claim-type-card ${formData.claimType === type.value ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, claimType: type.value }))}
          >
            <div className="claim-icon">{type.icon}</div>
            <div className="claim-content">
              <h3>{type.label}</h3>
              <p>{type.desc}</p>
            </div>
          </div>
        ))}
      </div>
      {errors.claimType && <span className="error-message">{errors.claimType}</span>}
      
      <div className="form-group">
        <label htmlFor="policyNumber">Policy Number *</label>
        <input
          type="text"
          id="policyNumber"
          name="policyNumber"
          value={formData.policyNumber}
          onChange={handleInputChange}
          placeholder="Enter your policy number"
          className={errors.policyNumber ? 'error' : ''}
        />
        {errors.policyNumber && <span className="error-message">{errors.policyNumber}</span>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step">
      <h2>Tell us about the incident</h2>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="dateOfIncident">Date of Incident *</label>
          <input
            type="date"
            id="dateOfIncident"
            name="dateOfIncident"
            value={formData.dateOfIncident}
            onChange={handleInputChange}
            max={new Date().toISOString().split('T')[0]}
            className={errors.dateOfIncident ? 'error' : ''}
          />
          {errors.dateOfIncident && <span className="error-message">{errors.dateOfIncident}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="timeOfIncident">Time of Incident *</label>
          <input
            type="time"
            id="timeOfIncident"
            name="timeOfIncident"
            value={formData.timeOfIncident}
            onChange={handleInputChange}
            className={errors.timeOfIncident ? 'error' : ''}
          />
          {errors.timeOfIncident && <span className="error-message">{errors.timeOfIncident}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="location">Location of Incident *</label>
        <input
          type="text"
          id="location"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          placeholder="Street address, city, state"
          className={errors.location ? 'error' : ''}
        />
        {errors.location && <span className="error-message">{errors.location}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="description">Detailed Description of Incident *</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Please provide a detailed description of what happened..."
          rows={6}
          className={errors.description ? 'error' : ''}
        />
        <div className="char-count">{formData.description.length}/500 characters</div>
        {errors.description && <span className="error-message">{errors.description}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="damageAmount">Estimated Damage Amount</label>
        <input
          type="number"
          id="damageAmount"
          name="damageAmount"
          value={formData.damageAmount}
          onChange={handleInputChange}
          placeholder="Enter estimated cost"
          min="0"
          step="0.01"
        />
      </div>

      <div className="form-group">
        <label htmlFor="witnesses">Witnesses (if any)</label>
        <textarea
          id="witnesses"
          name="witnesses"
          value={formData.witnesses}
          onChange={handleInputChange}
          placeholder="Names and contact information of any witnesses"
          rows={3}
        />
      </div>

      <div className="form-group checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="policeReport"
            checked={formData.policeReport}
            onChange={handleInputChange}
          />
          <span className="checkmark"></span>
          Police report was filed
        </label>
        
        {formData.policeReport && (
          <div className="form-group">
            <label htmlFor="policeReportNumber">Police Report Number</label>
            <input
              type="text"
              id="policeReportNumber"
              name="policeReportNumber"
              value={formData.policeReportNumber}
              onChange={handleInputChange}
              placeholder="Enter police report number"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-step">
      <h2>Contact Information</h2>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className={errors.firstName ? 'error' : ''}
          />
          {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className={errors.lastName ? 'error' : ''}
          />
          {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="email">Email Address *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="your.email@example.com"
          className={errors.email ? 'error' : ''}
        />
        {errors.email && <span className="error-message">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone Number *</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="(555) 123-4567"
          className={errors.phone ? 'error' : ''}
        />
        {errors.phone && <span className="error-message">{errors.phone}</span>}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-step">
      <h2>Upload Supporting Documents</h2>
      <p className="step-description">
        Please upload any relevant documents such as photos, receipts, reports, or other evidence.
      </p>
      
      <div className="file-upload-area">
        <div className="upload-zone">
          <input
            type="file"
            id="fileUpload"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <label htmlFor="fileUpload" className="upload-label">
            <div className="upload-icon">📎</div>
            <div className="upload-text">
              <h3>Drop files here or click to upload</h3>
              <p>Supported formats: Images, PDF, Word documents</p>
              <p>Maximum file size: 10MB per file</p>
            </div>
          </label>
        </div>
      </div>

      {formData.files.length > 0 && (
        <div className="uploaded-files">
          <h3>Uploaded Files ({formData.files.length})</h3>
          <div className="file-list">
            {formData.files.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="remove-file"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="file-guidelines">
        <h4>Document Guidelines:</h4>
        <ul>
          <li>📸 Photos of damage or incident scene</li>
          <li>📄 Police reports or incident reports</li>
          <li>💰 Receipts for repairs or medical expenses</li>
          <li>📋 Any correspondence related to the incident</li>
          <li>🆔 Copy of driver's license or ID (if applicable)</li>
        </ul>
      </div>
    </div>
  );

  const renderSuccessPage = () => (
    <div className="success-page">
      <div className="success-icon">🎉</div>
      <h2>Claim Successfully Submitted!</h2>
      <p>Thank you for filing your claim with PolicyPal. We've received all your information.</p>
      
      <div className="claim-summary">
        <div className="claim-number">
          <h3>Claim Number</h3>
          <div className="number">{claimSubmission?.claimNumber}</div>
        </div>
        
        <div className="claim-details">
          <div className="detail-row">
            <span className="label">Status:</span>
            <span className="value status">{claimSubmission?.status}</span>
          </div>
          <div className="detail-row">
            <span className="label">Submitted:</span>
            <span className="value">{claimSubmission?.submittedDate}</span>
          </div>
          <div className="detail-row">
            <span className="label">Estimated Processing:</span>
            <span className="value">{claimSubmission?.estimatedProcessingTime}</span>
          </div>
          <div className="detail-row">
            <span className="label">Claim Type:</span>
            <span className="value">{formData.claimType.charAt(0).toUpperCase() + formData.claimType.slice(1)} Insurance</span>
          </div>
        </div>
      </div>

      <div className="next-steps">
        <h3>What happens next?</h3>
        <div className="steps-timeline">
          <div className="timeline-item">
            <div className="timeline-icon">✅</div>
            <div className="timeline-content">
              <h4>Claim Received</h4>
              <p>Your claim has been submitted and assigned a number</p>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-icon">🔍</div>
            <div className="timeline-content">
              <h4>Initial Review</h4>
              <p>Our team will review your claim and may contact you for additional information</p>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-icon">📋</div>
            <div className="timeline-content">
              <h4>Investigation</h4>
              <p>We'll investigate the claim and assess the damages</p>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-icon">💰</div>
            <div className="timeline-content">
              <h4>Resolution</h4>
              <p>Claim decision and payment (if approved)</p>
            </div>
          </div>
        </div>
      </div>

      <div className="contact-info">
        <h4>Need Help?</h4>
        <p>📞 Claims Hotline: 1-800-CLAIMS-1</p>
        <p>✉️ Email: claims@policypal.com</p>
        <p>💬 Or use our chatbot for quick questions</p>
      </div>
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="claims-container">
        {renderSuccessPage()}
      </div>
    );
  }

  return (
    <div className="claims-container">
      <div className="claims-header">
        <h1>File an Insurance Claim</h1>
        <p>We're here to help you through the claims process. Please provide accurate information to ensure prompt processing.</p>
      </div>

      {renderStepIndicator()}

      <div className="claims-form">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-navigation">
            {currentStep > 1 && (
              <button type="button" onClick={handleBack} className="btn-secondary">
                ← Back
              </button>
            )}
            {currentStep < 4 ? (
              <button type="button" onClick={handleNext} className="btn-primary">
                Next →
              </button>
            ) : (
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? 'Submitting...' : 'Submit Claim'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClaimsForm;